import { useQuery } from '@tanstack/react-query'
import { AlarmClock, BookCopy, CircleDollarSign, Library } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { EmptyState, ErrorState, LoadingState } from '@/components/feedback/States'
import { Card, CardHeader } from '@/components/ui/Card'
import { PageHeader } from '@/components/ui/PageHeader'
import { StatCard } from '@/components/ui/StatCard'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs'
import { useAuth } from '@/features/auth/useAuth'
import { myLibraryQuery } from '@/features/library/api'
import { CatalogBrowser } from '@/features/library/components/CatalogBrowser'
import { LoanTable } from '@/features/library/components/LoanTable'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { formatMoney } from '@/utils/format'

function MyLoans() {
  const { t } = useTranslation('library')
  const { school } = useAuth()
  const currency = school?.currency ?? 'USD'
  const { data, isPending, error, refetch } = useQuery(myLibraryQuery())
  if (isPending) return <LoadingState />
  if (error) return <ErrorState onRetry={() => void refetch()} />
  if (!data.linked || !data.borrower)
    return (
      <Card>
        <EmptyState
          icon={<Library />}
          title={t('No staff record linked')}
          description={t(
            "Your login isn't linked to a staff record, so you have no library loans. You can still search the catalogue.",
          )}
        />
      </Card>
    )
  const b = data.borrower
  const open = data.loans.filter((l) => l.state !== 'returned')
  const history = data.loans.filter((l) => l.state === 'returned')
  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard label={t('On loan')} value={`${b.activeLoans} / ${b.loanLimit}`} icon={<BookCopy />} />
        <StatCard
          label={t('Overdue')}
          value={b.overdueLoans}
          icon={<AlarmClock />}
          tone={b.overdueLoans ? 'danger' : 'success'}
        />
        <StatCard
          label={t('Unpaid fines')}
          value={formatMoney(b.finesOutstanding, currency)}
          icon={<CircleDollarSign />}
          tone="warning"
        />
      </div>
      <Card>
        <CardHeader title={t('Books I have')} />
        <LoanTable
          caption={t('My current loans')}
          loans={open}
          currency={currency}
          showBorrower={false}
          empty={
            <EmptyState
              title={t('Nothing on loan')}
              description={t('Visit the library desk to borrow a book.')}
              compact
            />
          }
        />
      </Card>
      {history.length ? (
        <Card>
          <CardHeader title={t('Recently returned')} />
          <LoanTable
            caption={t('My loan history')}
            loans={history}
            currency={currency}
            showBorrower={false}
          />
        </Card>
      ) : null}
    </div>
  )
}

export function StaffLibraryPage() {
  const { t } = useTranslation('library')
  useDocumentTitle('Library')
  return (
    <>
      <PageHeader title={t('Library')} description={t('Your loans and the school catalogue.')} />
      <Tabs defaultValue="loans">
        <TabsList aria-label={t('Library sections')}>
          <TabsTrigger value="loans">{t('My loans')}</TabsTrigger>
          <TabsTrigger value="catalog">{t('Catalogue')}</TabsTrigger>
        </TabsList>
        <TabsContent value="loans">
          <MyLoans />
        </TabsContent>
        <TabsContent value="catalog">
          <CatalogBrowser />
        </TabsContent>
      </Tabs>
    </>
  )
}
